import { Wrench, Clock, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface MaintenanceSettings {
  message?: string;
  end_time?: string | null;
}

interface MaintenancePageProps {
  settings?: MaintenanceSettings;
  isAdminBypassing?: boolean;
}

export default function MaintenancePage({ settings, isAdminBypassing }: MaintenancePageProps) {
  const formatEndTime = (time: string) => {
    const date = new Date(time);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isAdminBypassing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4 gap-4">
        <Card className="max-w-lg w-full">
          <CardContent className="pt-8 pb-8 text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-warning/10 flex items-center justify-center">
              <Wrench className="h-8 w-8 text-warning" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-serif font-bold">Maintenance Active</h1>
              <p className="text-muted-foreground">
                You have admin access and can continue using the app.
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                Regular users see the maintenance page.
              </p>
            </div>

            <div className="pt-4">
              <Button 
                variant="link"
                onClick={() => window.location.href = '/dashboard'}
                className="text-primary hover:text-primary/80"
              >
                Go to dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <Card className="max-w-lg w-full">
        <CardContent className="pt-8 pb-8 text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Wrench className="h-8 w-8 text-primary animate-pulse" />
          </div>
          
          <div className="space-y-2">
            <h1 className="text-2xl font-serif font-bold">Under Maintenance</h1>
            <p className="text-muted-foreground">
              {settings?.message || 'We are working on improving the service. Please try again later.'}
            </p>
          </div>

          {settings?.end_time && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg py-3 px-4">
              <Clock className="h-4 w-4" />
              <span>Expected back: {formatEndTime(settings.end_time)}</span>
            </div>
          )}

          <div className="pt-4">
            <Button 
              variant="outline"
              onClick={() => window.location.reload()}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Try again
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}